import { ApiService } from './apiService';
import { API_ENDPOINTS } from '../config/api';
import { appendQueryString } from '../utils/queryParams';
import { parseDueDateFromApi } from '../utils/taskDate';
import { TaskCard } from '../types/kanban';
import {
  DashboardActivity,
  DashboardAlert,
  DashboardBirthday,
  SendBirthdayEmailResponse,
  DashboardData,
  DashboardStats,
  DashboardTask,
  DashboardTasksFilter,
  DashboardTasksGrouped,
  DashboardTasksResult,
  EMPTY_DASHBOARD_TASKS,
  EMPTY_DASHBOARD_TASKS_FILTER,
} from '../types/dashboard';

const STATUS_LABELS: Record<string, string> = {
  un_assigned: 'UNASSIGNED',
  todo: 'TO-DO',
  waiting_for_info: 'WAITING',
  in_progress: 'IN PROGRESS',
  completed: 'COMPLETED',
};

export const isBirthdayToday = (dateOfBirth: string): boolean => {
  const datePart = dateOfBirth.split('T')[0];
  const [, month, day] = datePart.split('-').map(Number);
  if (!month || !day) return false;
  const today = new Date();
  return today.getMonth() + 1 === month && today.getDate() === day;
};

export const formatDashboardDueDate = (apiDate: string | null | undefined): string => {
  const iso = parseDueDateFromApi(apiDate);
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return apiDate ?? '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const mapDashboardTask = (task: TaskCard): DashboardTask => ({
  id: String(task.taskId),
  taskId: String(task.taskId),
  title: task.title,
  assignee: {
    name: task.assignee?.name ?? 'Unassigned',
    avatarUrl: task.assignee?.avatarUrl ?? null,
  },
  dueDate: formatDashboardDueDate(task.dueDate),
  status: STATUS_LABELS[task.status] ?? task.status.replace(/_/g, ' ').toUpperCase(),
  completed: task.status === 'completed' || task.isClosed,
});

const mapTaskBucket = (bucket?: {
  total?: number;
  tasks?: TaskCard[];
}): { total: number; tasks: DashboardTask[] } => {
  const tasks = (bucket?.tasks ?? []).map(mapDashboardTask);
  return {
    total: bucket?.total ?? tasks.length,
    tasks,
  };
};

type DashboardTasksApiResponse = {
  filter?: DashboardTasksFilter;
  dueToday?: { total?: number; tasks?: TaskCard[] };
  dueTomorrow?: { total?: number; tasks?: TaskCard[] };
  dueIn7Days?: { total?: number; tasks?: TaskCard[] };
};

const mapTasksResponse = (data: DashboardTasksApiResponse): DashboardTasksGrouped => ({
  dueToday: mapTaskBucket(data.dueToday),
  dueTomorrow: mapTaskBucket(data.dueTomorrow),
  dueIn7Days: mapTaskBucket(data.dueIn7Days),
});

export const assigneeFilterToQueryValue = (
  assigneeUserId?: string
): number | undefined => {
  if (!assigneeUserId || assigneeUserId === 'all') return undefined;
  const id = Number(assigneeUserId);
  return Number.isNaN(id) ? undefined : id;
};

export const selectedAssigneeToFilterValue = (
  selectedAssigneeUserId: number | null
): string => (selectedAssigneeUserId === null ? 'all' : String(selectedAssigneeUserId));

const buildTasksUrl = (assigneeUserId?: string): string => {
  const queryValue = assigneeFilterToQueryValue(assigneeUserId);
  if (queryValue === undefined) {
    return API_ENDPOINTS.dashboard.tasks;
  }
  return appendQueryString(API_ENDPOINTS.dashboard.tasks, {
    assigneeUserId: queryValue,
  });
};

const DEFAULT_STATS: DashboardStats = {
  totalClients: 1284,
  paidClients: 942,
  totalLeads: 458,
  revenueSummary: 124500,
  activeSubscriptions: 812,
};

const DEFAULT_BIRTHDAYS: DashboardBirthday[] = [
  {
    userId: '101',
    name: 'Clara Smith',
    email: 'clara@example.com',
    occupation: 'Design Director',
    profilePictureUrl: null,
    dateOfBirth: '1990-06-02',
    daysUntil: 0,
    birthdayLabel: 'Today',
    isToday: true,
  },
  {
    userId: '205',
    name: 'Robert Chen',
    email: 'robert@example.com',
    occupation: 'Global Partners',
    profilePictureUrl: null,
    dateOfBirth: '1988-11-15',
    daysUntil: 5,
    birthdayLabel: 'Nov 15',
    isToday: false,
  },
];

const DEFAULT_TASKS: DashboardTasksGrouped = {
  dueToday: {
    total: 2,
    tasks: [
      {
        id: 'task-1',
        taskId: 'task-1',
        title: 'Client Onboarding - Acme Corp',
        assignee: { name: 'Alex M.', avatarUrl: null },
        dueDate: 'Oct 12',
        status: 'TO-DO',
        completed: false,
      },
      {
        id: 'task-2',
        taskId: 'task-2',
        title: 'Review Q3 Invoices',
        assignee: { name: 'Alex M.', avatarUrl: null },
        dueDate: 'Oct 12',
        status: 'TO-DO',
        completed: false,
      },
    ],
  },
  dueTomorrow: { total: 0, tasks: [] },
  dueIn7Days: { total: 0, tasks: [] },
};

const DEFAULT_ACTIVITY: DashboardActivity[] = [
  {
    id: 'activity-1',
    type: 'new_lead',
    title: 'New Lead added',
    description: 'Sarah Jenkins (Design Lab)',
    relativeTime: '2 hours ago',
    source: 'Automated Import',
    indicatorColor: 'green',
  },
  {
    id: 'activity-2',
    type: 'payment_received',
    title: 'Payment received',
    description: '$1,200.00 from Client X',
    relativeTime: '4 hours ago',
    source: 'Invoice #INV-8821',
    indicatorColor: 'blue',
  },
  {
    id: 'activity-3',
    type: 'contract_updated',
    title: 'Contract Updated',
    description: 'Pithad Solutions & Global Partners',
    relativeTime: 'Yesterday',
    source: 'Modified by Admin',
    indicatorColor: 'orange',
  },
];

const DEFAULT_ALERTS: DashboardAlert[] = [
  {
    id: 'alert-1',
    title: "Today's Deadline",
    message: 'Invoice finalization for 12 enterprise clients due by 5 PM.',
    severity: 'warning',
  },
  {
    id: 'alert-2',
    title: '3 New Leads',
    message: 'Awaiting relationship manager assignment in the Kanban board.',
    severity: 'info',
  },
];

export class DashboardService extends ApiService {
  async getStats(): Promise<DashboardStats> {
    try {
      return await this.get<DashboardStats>(API_ENDPOINTS.dashboard.stats);
    } catch {
      return DEFAULT_STATS;
    }
  }

  async getUpcomingBirthdays(): Promise<DashboardBirthday[]> {
    try {
      return await this.get<DashboardBirthday[]>(API_ENDPOINTS.dashboard.upcomingBirthdays);
    } catch {
      return DEFAULT_BIRTHDAYS;
    }
  }

  async sendBirthdayEmail(userId: string): Promise<SendBirthdayEmailResponse> {
    return this.post<SendBirthdayEmailResponse>(
      API_ENDPOINTS.dashboard.sendBirthdayEmail(userId)
    );
  }

  async getTasks(assigneeUserId?: string): Promise<DashboardTasksResult> {
    try {
      const data = await this.get<DashboardTasksApiResponse>(buildTasksUrl(assigneeUserId));
      return {
        filter: data.filter ?? EMPTY_DASHBOARD_TASKS_FILTER,
        tasks: mapTasksResponse(data),
      };
    } catch {
      return {
        filter: EMPTY_DASHBOARD_TASKS_FILTER,
        tasks: DEFAULT_TASKS,
      };
    }
  }

  async getActivityFeed(): Promise<DashboardActivity[]> {
    try {
      return await this.get<DashboardActivity[]>(API_ENDPOINTS.dashboard.activityFeed);
    } catch {
      return DEFAULT_ACTIVITY;
    }
  }

  async getAlerts(): Promise<DashboardAlert[]> {
    try {
      return await this.get<DashboardAlert[]>(API_ENDPOINTS.dashboard.alerts);
    } catch {
      return DEFAULT_ALERTS;
    }
  }

  /** Loads all five dashboard endpoints in parallel (per integration guide). */
  async fetchAll(assigneeUserId?: string): Promise<DashboardData & { tasksFilter: DashboardTasksFilter }> {
    const [stats, birthdays, tasksResult, activity, alerts] = await Promise.all([
      this.getStats(),
      this.getUpcomingBirthdays(),
      this.getTasks(assigneeUserId),
      this.getActivityFeed(),
      this.getAlerts(),
    ]);
    return {
      stats,
      birthdays,
      tasks: tasksResult.tasks,
      tasksFilter: tasksResult.filter,
      activity,
      alerts,
    };
  }
}

export const dashboardService = new DashboardService();
